import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  participationStats,
  verificationPrinciples,
  participationRecords,
  participationNote,
} from '@/mocks/participation';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import ParticipationRecord from './components/ParticipationRecord';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

const documentedFairs = participationRecords.filter((record) => record.startDate && record.endDate);

const participationSchema = {
  '@type': 'CollectionPage',
  name: 'Verified Book Fair Participation',
  url: 'https://leavittpeakpress.com/events/verified-participation',
  description:
    'A documented record of the book fairs Leavitt Peak Press has exhibited at, with venues, dates, stand details, and dated photographs taken on the floor.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Leavitt Peak Press',
    url: 'https://leavittpeakpress.com/',
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: documentedFairs.map((record, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Event',
        name: `${record.fair} ${record.edition} - Leavitt Peak Press stand`,
        startDate: record.startDate,
        endDate: record.endDate,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        description: record.summary,
        image: record.photos[0],
        organizer: { '@type': 'Organization', name: record.fair },
        location: {
          '@type': 'Place',
          name: record.venue,
          address: record.city,
        },
      },
    })),
  },
};

export default function VerifiedParticipation() {
  const [lightbox, setLightbox] = useState<{ src: string; caption: string } | null>(null);

  const heroAnim = useScrollAnimation({ threshold: 0.1 });
  const statsAnim = useScrollAnimation({ threshold: 0.15 });
  const principlesAnim = useScrollAnimation({ threshold: 0.1 });
  const recordsAnim = useScrollAnimation({ threshold: 0.05 });
  const noteAnim = useScrollAnimation({ threshold: 0.2 });
  const ctaAnim = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <main>
      <Seo {...pageSeo.verifiedParticipation} schema={participationSchema} />
      {/* ============ HERO ============ */}
      <section className="relative w-full min-h-[460px] md:min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20editorial%20background%20of%20a%20vast%20international%20book%20fair%20hall%20rendered%20as%20soft%20layered%20gold%20and%20deep%20navy%20light%2C%20blurred%20rows%20of%20exhibition%20stands%20fading%20into%20haze%2C%20cinematic%20depth%2C%20elegant%20minimal%20geometry%2C%20refined%20literary%20atmosphere%2C%20no%20readable%20text%2C%20premium%20photographic%20quality&width=1920&height=900&seq=verified-participation-hero-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-900/75 to-navy-950/90"></div>
        </div>

        <div
          ref={heroAnim.ref}
          className={`w-full px-4 md:px-6 lg:px-8 relative z-10 py-16 md:py-20 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/15 border border-gold-400/30 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-gold-400"></span>
              <span className="text-[11px] font-medium text-gold-300 uppercase tracking-[0.18em]">
                Evidence, Not Adjectives
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              Verified <span className="text-gold-400">Participation</span>
            </h1>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed max-w-2xl mx-auto mb-8">
              We say we take authors&rsquo; titles to international book fairs. This page is where that
              sentence has to prove itself &mdash; fair by fair, year by year, with the stand we held and
              the photographs we took on the floor during those dates.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#the-record"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-colors duration-300 whitespace-nowrap"
              >
                See the record
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
              <Link
                to="/about-us#our-honest-position"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-colors duration-300 whitespace-nowrap"
              >
                Read our honest position
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="w-full py-10 md:py-12 bg-white border-b border-cream-200">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={statsAnim.ref}
            className={`max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 animate-on-scroll ${statsAnim.isVisible ? 'animate-visible' : ''}`}
          >
            {participationStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-xl bg-gold-50 text-gold-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-navy-900 leading-none mb-1.5">
                  {stat.value}
                </p>
                <p className="text-xs text-navy-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW WE DOCUMENT IT ============ */}
      <section className="w-full py-14 md:py-20 bg-cream-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={principlesAnim.ref}
            className={`max-w-6xl mx-auto animate-on-scroll ${principlesAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="max-w-2xl mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
                How Verification Works
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy-900 leading-tight mb-4">
                Three things we will always be able to show you
              </h2>
              <p className="text-sm text-navy-600 leading-relaxed">
                Any press can write the word &ldquo;international&rdquo; on a website. These are the three
                standards we hold our own participation claims to, and the same three we will show you if
                you ask.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {verificationPrinciples.map((item, idx) => (
                <div
                  key={item.title}
                  className="bg-white border border-cream-200 rounded-2xl p-6 hover:border-gold-300 transition-colors duration-500"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold-50 text-gold-600 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <span className="text-[11px] font-semibold text-navy-500 uppercase tracking-widest">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-navy-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE RECORD ============ */}
      <section id="the-record" className="w-full py-14 md:py-20 bg-white scroll-mt-20">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={recordsAnim.ref}
            className={`max-w-6xl mx-auto animate-on-scroll ${recordsAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-10">
              <div className="max-w-2xl">
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
                  The Record
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900 leading-tight">
                  Every fair we can document
                </h2>
                <div className="w-16 h-1 bg-gold-400 rounded-full mt-4 mb-4"></div>
                <p className="text-sm text-navy-600 leading-relaxed">
                  Seven appearances we can stand behind with photographs and booking records. Where we hold
                  a stand number, it is printed below. Where we do not, we say so instead of guessing.
                </p>
              </div>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-navy-600 border border-cream-200 rounded-full hover:border-gold-300 hover:text-gold-700 transition-colors duration-300 whitespace-nowrap self-start md:self-auto"
              >
                <i className="ri-gallery-line"></i>
                Full photo archive
              </Link>
            </div>

            <div className="space-y-6 md:space-y-8">
              {participationRecords.map((record, idx) => (
                <ParticipationRecord
                  key={record.id}
                  record={record}
                  index={idx}
                  onPhotoClick={(src, caption) => setLightbox({ src, caption })}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT THIS PAGE IS NOT ============ */}
      <section className="w-full py-14 md:py-16 bg-navy-900">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={noteAnim.ref}
            className={`max-w-4xl mx-auto animate-on-scroll ${noteAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="flex items-start gap-4 mb-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold-500/20 text-gold-400 shrink-0">
                <i className="ri-shield-check-line text-xl"></i>
              </span>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">{participationNote.title}</h2>
                <p className="text-xs text-cream-300 mt-1">
                  Because a verification page has to be able to fail.
                </p>
              </div>
            </div>
            <div className="space-y-4 text-sm md:text-[15px] text-cream-200 leading-relaxed">
              {participationNote.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>
            <div className="mt-7 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-sm text-gold-300 font-medium">
                Want the booth confirmation for a specific year? Just ask.
              </p>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-colors duration-300 whitespace-nowrap"
              >
                Request documentation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="w-full py-14 md:py-16 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={ctaAnim.ref}
            className={`max-w-4xl mx-auto text-center animate-on-scroll ${ctaAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">
              Your title at the next stand
            </h2>
            <p className="text-sm md:text-base text-navy-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              If you want your book on the table at the next fair, we can tell you exactly what that
              involves &mdash; which fairs, which stand, which catalogue, and what it costs. What we will not
              tell you is how many copies it will sell.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact-us"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-colors duration-300 whitespace-nowrap"
              >
                Talk to us about your title
              </Link>
              <Link
                to="/about-us#our-honest-position"
                className="w-full sm:w-auto px-7 py-3.5 text-sm font-medium text-navy-600 border border-cream-200 rounded-full hover:border-gold-300 hover:text-gold-700 transition-colors duration-300 whitespace-nowrap"
              >
                Read our honest position
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ LIGHTBOX ============ */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-navy-950/95 flex items-center justify-center p-4 md:p-8 animate-fade-in"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative w-full max-w-5xl animate-scale-in"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.caption}
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
            />
            <div className="flex items-start justify-between gap-4 mt-3">
              <p className="text-xs text-cream-300 leading-relaxed max-w-2xl">{lightbox.caption}</p>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-300 shrink-0 cursor-pointer"
                aria-label="Close photograph"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}